import { project as P } from './config/project.js';

function esc(s = '') {
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

export function ThankYouPage() {
  return `
    <div class="app thank-you-app">
      <a class="skip-link" href="#main">Skip to content</a>
      <header class="site-header">
        <div class="container header-inner">
          <a href="/" class="brand-link" aria-label="Cornerstone home">
            <img src="/assets/logos/Cornerstone_Logo_Horizontal_BrandBlack.svg" alt="Cornerstone" width="180" height="48" />
          </a>
          <div class="header-end">
            <a href="/#register" class="btn btn-primary">Register</a>
          </div>
        </div>
      </header>
      <main id="main" class="thank-you">
        <div class="container narrow">
          <p class="eyebrow">You’re on the list</p>
          <h1>Thank you for registering</h1>
          <p>We’ll follow up with Cornerstone updates on pricing, floor plans, and community news. If you requested a download, it should begin shortly — you can also use the secure link from the confirmation screen.</p>
          <div class="cta-row">
            <a class="btn btn-primary" href="/#overview">Back to overview</a>
            <a class="btn btn-ghost" href="/#downloads">Browse downloads</a>
          </div>
        </div>
      </main>
      ${Footer()}
    </div>
  `;
}

function PhoneLink({ className = 'phone-link', label } = {}) {
  if (!P.phoneHref) return '';
  return `<a class="${className}" href="${esc(P.phoneHref)}" data-analytics="phone_click">${esc(label || P.phone)}</a>`;
}

function Footer() {
  return `
    <footer class="site-footer" id="footer">
      <div class="container footer-grid">
        <div>
          <img class="footer-logo" src="/assets/logos/Cornerstone_Logo_Horizontal_BrandBlack.svg" alt="Cornerstone" width="160" height="42" />
          <p class="footer-brand">${esc(P.name)} by ${esc(P.builder)}</p>
          <p class="footer-pub">${esc(P.area)}, Ontario</p>
          <p class="footer-nap">
            ${PhoneLink({ className: 'footer-phone' })}
          </p>
          <p class="footer-contact">
            <a href="/#register">Register for updates</a>
          </p>
        </div>
        <nav aria-label="Footer">
          <a href="/#overview">Overview</a>
          <a href="/#homes">Homes</a>
          <a href="/#gallery">Gallery</a>
          <a href="/#register">Register</a>
          <a href="/brampton-new-townhomes">New townhomes in Brampton</a>
          <a href="/northwest-brampton-new-homes">Northwest Brampton new homes</a>
          <a href="/hst-rebate-new-homes-ontario">HST rebate on new homes</a>
          <a href="/privacy-policy">Privacy policy</a>
        </nav>
      </div>
      <div class="container">
        <p class="disclaimer">${esc(P.legal.disclaimer)}</p>
        <p class="disclaimer subtle">${esc(P.legal.marketingNote)}</p>
        <p class="disclaimer micro">${esc(P.legal.representationNote)}</p>
        <p class="copyright">© ${P.legal.copyrightYear} Independent marketing site for ${esc(P.name)} by ${esc(P.builder)}. Not an official builder website.</p>
      </div>
    </footer>
  `;
}

export default function App() {
  const downloads = P.downloads
    .map(
      (d) => `
      <li class="download-item">
        <div>
          <h3>${esc(d.title)}</h3>
          <p>${esc(d.description)}</p>
          <p class="meta">${esc(d.type)}${d.lastUpdated ? ` · Last updated ${esc(d.lastUpdated)}` : ''}</p>
        </div>
        <button type="button" class="btn btn-dark" data-download="${esc(d.id)}" data-analytics="download_request">
          Request ${esc(d.title)}
        </button>
      </li>`,
    )
    .join('');

  const homes = P.homes
    .map(
      (h) => `
      <article class="home-block" id="${esc(h.id)}">
        <h3>${esc(h.title)}</h3>
        <p>${esc(h.summary)}</p>
        <button type="button" class="btn btn-dark" data-download="${esc(h.downloadId)}">${esc(h.cta)}</button>
      </article>`,
    )
    .join('');

  const highlights = P.highlights
    .map(
      (h) => `
      <li>
        <h3>${esc(h.title)}</h3>
        <p>${esc(h.text)}</p>
      </li>`,
    )
    .join('');

  const gallery = P.gallery
    .map(
      (img) => {
        const sizes = '(max-width:768px) 100vw, 50vw';
        const webpSet = img.webpSrcSet || (img.srcSet ? img.srcSet.replaceAll('.jpg', '.webp') : '');
        const imgTag = `
        <img
          src="${esc(img.src)}"
          ${img.srcSet ? `srcset="${esc(img.srcSet)}" sizes="${sizes}"` : ''}
          alt="${esc(img.alt)}"
          width="${img.width || 1200}"
          height="${img.height || 800}"
          loading="lazy"
          decoding="async"
        />`;
        const media = webpSet
          ? `<picture>
              <source type="image/webp" srcset="${esc(webpSet)}" sizes="${sizes}" />
              ${imgTag}
            </picture>`
          : imgTag;
        return `
      <figure class="gallery-item gallery-item--${img.fit === 'contain' ? 'contain' : 'cover'}${img.span === 'full' ? ' gallery-item--full' : ''}${img.width === img.height ? ' gallery-item--square' : ''}">
        ${media}
      </figure>`;
      },
    )
    .join('');

  const faq = P.faq
    .map(
      (item, i) => `
      <details class="faq-item"${i === 0 ? ' open' : ''}>
        <summary>${esc(item.q)}</summary>
        <p>${item.html || esc(item.a)}</p>
      </details>`,
    )
    .join('');

  return `
    <div class="app">
      <a class="skip-link" href="#main">Skip to content</a>

      <header class="site-header" id="top">
        <div class="container header-inner">
          <a href="/" class="brand-link" aria-label="Cornerstone by Primont home">
            <img src="/assets/logos/Cornerstone_Logo_Horizontal_BrandBlack.svg" alt="Cornerstone" width="170" height="46" fetchpriority="high" />
          </a>
          <div class="header-end">
            <button type="button" class="nav-toggle" aria-expanded="false" aria-controls="primary-nav" id="nav-toggle">
              <span class="sr-only">Menu</span>
              <span aria-hidden="true"></span>
            </button>
            <nav class="primary-nav" id="primary-nav" aria-label="Primary">
              <a href="/#overview" data-nav>Overview</a>
              <a href="/#homes" data-nav>Homes</a>
              <a href="/#gallery" data-nav>Gallery</a>
              <a href="/#site-plan" data-nav>Site plan</a>
              <a href="/#location" data-nav>Location</a>
              <a href="/#downloads" data-nav>Downloads</a>
              <a href="/register" class="btn btn-primary" data-analytics="register_cta_click">Register</a>
            </nav>
          </div>
        </div>
      </header>
      <button type="button" class="nav-backdrop" id="nav-backdrop" aria-label="Close menu" hidden></button>

      <main id="main">
        <section class="hero" aria-labelledby="hero-heading">
          <div class="hero-panel">
            <img class="hero-mark" src="/assets/logos/Cornerstone_Logo_Vertical_BrandBlack.svg" alt="Cornerstone" width="160" height="180" />
            <h1 id="hero-heading" class="hero-eyebrow hero-h1">${esc(P.hero.h1)}</h1>
            <p class="hero-title">${esc(P.hero.headline)}</p>
            <p class="hero-support">${esc(P.hero.support)}</p>
            <div class="hero-actions">
              <a href="/register" class="btn btn-dark" data-analytics="register_cta_click">${esc(P.hero.primaryCta)}</a>
              <a href="#video" class="btn btn-ghost-on-brand">${esc(P.hero.secondaryCta)}</a>
            </div>
            <p class="hero-note">${esc(P.hero.artistNote)} Status last verified ${esc(P.status.lastVerified)}.</p>
          </div>
          <div class="hero-media">
            <picture>
              <source type="image/webp" srcset="/assets/web/hero-render-640.webp 640w, /assets/web/hero-render-960.webp 960w, /assets/web/hero-render-1280.webp 1280w, /assets/web/hero-render-1600.webp 1600w" sizes="(max-width:900px) 100vw, 55vw" />
              <img
                src="/assets/web/hero-render-1280.jpg"
                srcset="/assets/web/hero-render-640.jpg 640w, /assets/web/hero-render-960.jpg 960w, /assets/web/hero-render-1280.jpg 1280w, /assets/web/hero-render-1600.jpg 1600w"
                sizes="(max-width:900px) 100vw, 55vw"
                alt="Cornerstone by Primont community rendering — artist’s concept"
                width="1600"
                height="1080"
                fetchpriority="high"
                loading="eager"
                decoding="async"
              />
            </picture>
          </div>
        </section>

        <section class="section story" id="overview">
          <div class="container narrow">
            <h2>${esc(P.story.title)}</h2>
            <p class="lead">${esc(P.story.lead)}</p>
            <p class="direct-answer">${esc(P.story.answer)}</p>
            ${P.story.paragraphs.map((p) => `<p>${esc(p)}</p>`).join('')}
            <p class="closing">${esc(P.story.closing)}</p>
            <a href="/register" class="btn btn-primary" data-analytics="register_cta_click">Stay in the loop</a>
          </div>
        </section>

        <section class="section section-alt" id="homes">
          <div class="container">
            <h2>Homes at Cornerstone</h2>
            <p class="section-intro">${esc(P.productNote)} Public pricing currently starts ${esc(P.status.publicPriceMessage)}.</p>
            <div class="homes-grid">${homes}</div>
          </div>
        </section>

        <section class="section" id="highlights">
          <div class="container">
            <h2>Features & finishes highlights</h2>
            <p class="section-intro">High-level selections from the authorized features sheet. Full specifications are provided in the gated PDF.</p>
            <ul class="highlight-list">${highlights}</ul>
            <button type="button" class="btn btn-dark" data-download="features">Request full feature sheet</button>
          </div>
        </section>

        <section class="section section-alt" id="gallery">
          <div class="container">
            <h2>Gallery</h2>
            <p class="section-intro">Authorized renderings and creatives. Artist’s concepts — subject to change.</p>
            <div class="gallery-grid">${gallery}</div>
          </div>
        </section>

        <section class="section" id="video">
          <div class="container">
            <h2>Community video</h2>
            <div class="video-wrap">
              <video
                id="community-video"
                controls
                preload="none"
                poster="${esc(P.video.poster)}"
                data-analytics-video
              >
                <source src="${esc(P.video.teaserSrc)}" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <p><a href="${esc(P.video.youtubeUrl)}" rel="noopener noreferrer" target="_blank">Watch the full community video on YouTube</a></p>
          </div>
        </section>

        <section class="section section-alt" id="site-plan">
          <div class="container">
            <h2>Site plan</h2>
            <p class="section-intro">${esc(P.sitePlan.note)}</p>
            <figure class="siteplan-preview">
              <img
                src="${esc(P.sitePlan.preview)}"
                srcset="${esc(P.sitePlan.previewSrcSet)}"
                sizes="(max-width:768px) 100vw, 900px"
                alt="${esc(P.sitePlan.alt)}"
                width="1400"
                height="900"
                loading="lazy"
                data-analytics-view="siteplan_view"
              />
            </figure>
            <button type="button" class="btn btn-dark" data-download="siteplan">Request site plan PDF</button>
          </div>
        </section>

        <section class="section" id="location">
          <div class="container location-grid">
            <div>
              <h2>${esc(P.location.title)}</h2>
              <p>${esc(P.location.summary)}</p>
              <ul>${P.location.points.map((p) => `<li>${esc(p)}</li>`).join('')}</ul>
              <a href="/#register" class="btn btn-primary" data-analytics="register_cta_click">Register for VIP updates</a>
            </div>
            <div class="map-embed" role="region" aria-label="Map of Northwest Brampton">
              <iframe
                title="Map of Northwest Brampton"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=${encodeURIComponent(P.location.mapQuery)}&output=embed"
              ></iframe>
            </div>
          </div>
        </section>

        <section class="section section-alt" id="downloads">
          <div class="container">
            <h2>Download centre</h2>
            <p class="section-intro">Resources unlock after a short registration so we can send VIP updates. Broker-only worksheets and sample APS documents are not published here.</p>
            <ul class="download-list">${downloads}</ul>
          </div>
        </section>

        <section class="section register-section" id="register">
          <div class="container narrow">
            <h2>Stay in the loop</h2>
            <p>Register to be among the first to receive pricing, incentives, floor plans, and community updates for Cornerstone by Primont.</p>
            <p id="ai-source-note" class="hint" hidden></p>
            <div id="form-status" class="form-status" role="status" aria-live="polite"></div>
            <div id="form-error-summary" class="error-summary" hidden tabindex="-1"></div>
            <form id="register-form" class="register-form" novalidate>
              <input type="text" name="website" class="hp" tabindex="-1" autocomplete="off" aria-hidden="true" />
              <input type="hidden" name="formType" value="registration" />
              <input type="hidden" name="downloadId" id="download-id-field" value="" />
              <div class="form-row">
                <div class="field">
                  <label for="firstName">First name</label>
                  <input id="firstName" name="firstName" type="text" autocomplete="given-name" required />
                </div>
                <div class="field">
                  <label for="lastName">Last name</label>
                  <input id="lastName" name="lastName" type="text" autocomplete="family-name" required />
                </div>
              </div>
              <div class="form-row">
                <div class="field">
                  <label for="email">Email</label>
                  <input id="email" name="email" type="email" autocomplete="email" required />
                </div>
                <div class="field">
                  <label for="phone">Phone</label>
                  <input id="phone" name="phone" type="tel" autocomplete="tel" inputmode="tel" required aria-describedby="phone-hint" />
                </div>
              </div>
              <p id="phone-hint" class="hint form-row-hint">Canadian numbers format as XXX-XXX-XXXX; international numbers are accepted.</p>
              <fieldset class="field field-choice">
                <legend>Are you a realtor?</legend>
                <div class="choice-row">
                  <label class="inline"><input type="radio" name="isRealtor" value="yes" required /> Yes</label>
                  <label class="inline"><input type="radio" name="isRealtor" value="no" /> No</label>
                </div>
              </fieldset>
              <div class="field" id="brokerage-field" hidden>
                <label for="brokerage">Brokerage / company</label>
                <input id="brokerage" name="brokerage" type="text" autocomplete="organization" />
              </div>
              <div class="field">
                <label for="productInterest">Product interest</label>
                <select id="productInterest" name="productInterest" required>
                  <option value="">Select…</option>
                  <option value="Townhomes">Townhomes</option>
                  <option value="Detached Homes">Detached Homes</option>
                  <option value="Both">Both</option>
                  <option value="Not sure">Not sure yet</option>
                </select>
              </div>
              <div class="field">
                <label for="buyerType">What best describes you?</label>
                <select id="buyerType" name="buyerType" required>
                  <option value="">Select…</option>
                  <option>First Time Home Buyer</option>
                  <option>Move Up Buyer</option>
                  <option>Downsizing</option>
                  <option>Investor</option>
                  <option>Realtor / Broker</option>
                </select>
              </div>
              <label class="consent">
                <input type="checkbox" name="marketingConsent" value="yes" required />
                <span>I consent to be contacted about Cornerstone and related new-home opportunities through the information I submit on this form. See our <a href="/privacy-policy">privacy policy</a>.</span>
              </label>
              <button type="submit" class="btn btn-primary btn-block" id="submit-btn">Submit registration</button>
              <p class="form-footnote">Protected against spam. We never store your details in browser local storage.</p>
            </form>
          </div>
        </section>

        <section class="section section-alt" id="faq">
          <div class="container narrow">
            <h2>FAQ</h2>
            ${faq}
          </div>
        </section>
      </main>

      ${Footer()}

      <div class="modal" id="download-modal" hidden>
        <div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="download-modal-title">
          <button type="button" class="modal-close" aria-label="Close dialog">&times;</button>
          <h2 id="download-modal-title">Request download</h2>
          <p>Complete the registration form to unlock <strong id="download-type-label">this resource</strong>.</p>
          <button type="button" class="btn btn-primary" id="download-goto-form">Continue to register</button>
        </div>
      </div>
    </div>
  `;
}
